import { Link } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { notifyTelegram } from '@/lib/notifyTelegram';
import { toast } from 'sonner';
import { Send, Send as TelegramIcon, MessageCircle } from 'lucide-react';

export function Footer() {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = formData.name.trim();
    const phone = formData.phone.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();

    if (!name || !phone || !email || !message) {
      toast.error('Please fill in all fields.');
      return;
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (name.length > 100 || phone.length > 30 || email.length > 255 || message.length > 1000) {
      toast.error('One or more fields exceed the maximum length.');
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.from('contact_messages').insert({ name, phone, email, message });

    if (error) {
      toast.error('Failed to send message. Please try again.');
    } else {
      notifyTelegram('New Contact Message', `From: ${name}\nPhone: ${phone}\nEmail: ${email}\n\n${message}`);
      toast.success('Message sent successfully!');
      setFormData({ name: '', phone: '', email: '', message: '' });
    }
    setIsSubmitting(false);
  };

  return (
    <footer className="border-t border-border bg-background">
      <div className="container-library py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <span className="font-serif text-lg tracking-wide text-foreground">
              THE PACERS LIBRARY
            </span>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              A curated digital library for discovering books by independent and published authors worldwide.
            </p>
          </div>

          {/* Navigation */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">
              Explore
            </h4>
            <nav className="flex flex-col gap-2">
              <Link to="/library" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Browse Library
              </Link>
              <Link to="/books" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Featured Books
              </Link>
              <Link to="/authors" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Author Libraries
              </Link>
              <Link to="/what-we-do" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Our Mission
              </Link>
            </nav>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">
              Support
            </h4>
            <p className="text-xs text-muted-foreground">
              Reach our team directly on Telegram or WhatsApp.
            </p>
            <nav className="flex flex-col gap-2">
              <a href="https://t.me/PACERS_LIBRARY" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                <TelegramIcon size={16} />
                Telegram
              </a>
              <a href="https://wa.me/12202019541" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                <MessageCircle size={16} />
                WhatsApp
              </a>
            </nav>
          </div>

          {/* Contact Form */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">
              Send Us a Message
            </h4>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                maxLength={100}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-secondary text-foreground border border-border rounded-sm 
                         placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                maxLength={30}
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-secondary text-foreground border border-border rounded-sm 
                         placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <input
                type="email"
                placeholder="Email Address"
                maxLength={255}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-secondary text-foreground border border-border rounded-sm 
                         placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <textarea
                placeholder="Your Message"
                maxLength={1000}
                rows={3}
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-secondary text-foreground border border-border rounded-sm 
                         placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-sm 
                         hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <Send size={14} />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        <div className="divider my-8" />

        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            © 2026 PACERS AGENCY™. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
